/**
 * Encoding for the chart's symbol string, which the Datafeed routes on.
 *
 * Grammar:
 *   TVSymbolId := <kind> ":" <id>
 *
 *   kind   ∈ { "token", "perp", "pred", "inline" }
 *   token  : <standardizedInstrumentId>
 *   perp   : <standardizedInstrumentId>
 *   pred   : <standardizedInstrumentId> "/" <outcomeTokenId>
 *   inline : <opaque per-mount handle>
 *
 * `outcomeTokenId` may contain arbitrary characters, so `parseTVSymbol`
 * URL-decodes after the first `/`. `encodeTVSymbol` URL-encodes the same
 * boundary. This keeps the symbol string a single token across Charting
 * Library APIs that treat it as opaque (`setSymbol`, `LibrarySymbolInfo.ticker`).
 */
import { notEmpty } from "@orb-labs/types";
export var TVSymbolKind;
(function (TVSymbolKind) {
    TVSymbolKind["TOKEN"] = "token";
    TVSymbolKind["PERP"] = "perp";
    TVSymbolKind["PRED"] = "pred";
    TVSymbolKind["INLINE"] = "inline";
})(TVSymbolKind || (TVSymbolKind = {}));
export function encodeTVSymbol(input) {
    switch (input.kind) {
        case TVSymbolKind.TOKEN:
        case TVSymbolKind.PERP: {
            requireNonEmpty("standardizedInstrumentId", input.standardizedInstrumentId);
            return `${input.kind}:${input.standardizedInstrumentId}`;
        }
        case TVSymbolKind.PRED: {
            requireNonEmpty("standardizedInstrumentId", input.standardizedInstrumentId);
            requireNonEmpty("outcomeTokenId", input.outcomeTokenId);
            return `${TVSymbolKind.PRED}:${input.standardizedInstrumentId}/${encodeURIComponent(input.outcomeTokenId)}`;
        }
        case TVSymbolKind.INLINE: {
            requireNonEmpty("handle", input.handle);
            return `${TVSymbolKind.INLINE}:${input.handle}`;
        }
        default:
            // Exhaustiveness guard: the discriminated union above covers every
            // TVSymbolKind value, so this branch is unreachable. If a new kind is
            // added without a case here, the `never` cast becomes a compile error.
            throw new Error(`encodeTVSymbol: unhandled kind ${input.kind}`);
    }
}
export function parseTVSymbol(symbol) {
    if (!notEmpty(symbol) || typeof symbol !== "string") {
        throw new Error(`parseTVSymbol: empty or non-string input`);
    }
    const colon = symbol.indexOf(":");
    if (colon < 0) {
        throw new Error(`parseTVSymbol: missing ":" in "${symbol}"`);
    }
    const kindPart = symbol.slice(0, colon);
    const idPart = symbol.slice(colon + 1);
    if (!idPart) {
        throw new Error(`parseTVSymbol: empty id in "${symbol}"`);
    }
    switch (kindPart) {
        case TVSymbolKind.TOKEN:
            return { kind: TVSymbolKind.TOKEN, standardizedInstrumentId: idPart };
        case TVSymbolKind.PERP:
            return { kind: TVSymbolKind.PERP, standardizedInstrumentId: idPart };
        case TVSymbolKind.PRED: {
            const slash = idPart.indexOf("/");
            if (slash < 0) {
                throw new Error(`parseTVSymbol: pred missing "/<outcomeTokenId>" in "${symbol}"`);
            }
            const standardizedInstrumentId = idPart.slice(0, slash);
            const rawOutcome = idPart.slice(slash + 1);
            if (!standardizedInstrumentId || !rawOutcome) {
                throw new Error(`parseTVSymbol: pred missing id or outcome in "${symbol}"`);
            }
            return {
                kind: TVSymbolKind.PRED,
                standardizedInstrumentId,
                outcomeTokenId: decodeURIComponent(rawOutcome),
            };
        }
        case TVSymbolKind.INLINE:
            return { kind: TVSymbolKind.INLINE, handle: idPart };
        default:
            throw new Error(`parseTVSymbol: unknown kind "${kindPart}" in "${symbol}"`);
    }
}
function requireNonEmpty(field, value) {
    if (!notEmpty(value) || value.length === 0) {
        throw new Error(`encodeTVSymbol: empty ${field}`);
    }
}
